import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    offers: 'Offers',
    orders: 'Orders',
    closedOrders: 'Closed Orders',
    historyView: 'View',
    historyEdit: 'Edit',
    historyRevoke: 'Reopen',
    completedAt: 'Completed at',
    actions: 'Actions',
    reopenOrderConfirm:
      'Revoke completion? The order will return to the active orders and offers list.',
    reopenOrderSuccess: 'Completion revoked; the order is active again.',
    reopenOrderFailed: 'Could not revoke completion',
    teams: 'Teams',
    planning: 'Planning',
    service: 'Service',
    servicePageSubtitle: 'Service orders after opleverbon restpunten or when a closed order is reopened.',
    serviceActiveSection: 'Active service',
    serviceArchiveSection: 'Completed service',
    serviceStatusColumn: 'Service status',
    serviceRoundsColumn: 'Service rounds',
    serviceRoundCount: (n) => (n === 1 ? '1 round' : `${n} rounds`),
    serviceOpenServicebon: 'Servicebon',
    serviceNoActive: 'No active service orders.',
    serviceNoArchive: 'No completed orders with servicebon history.',
    serviceStatusAwaitingSign: 'Awaiting servicebon',
    serviceStatusInProgress: 'Service in progress',
    serviceStatusShortNeedText: 'Open',
    serviceStatusShortSigned: 'Signed on mobile',
    serviceStatusShortAwaiting: 'Awaiting servicebon',
    serviceStatusShortProgress: 'Service in progress',
    navServiceAttentionTitle: 'Active service orders need attention',
    users: 'Users',
    auditTrail: 'Audit trail',
    adminSection: 'Administration',
    adminOnlyPage: 'Admin access only',
    usersPageSubtitle: 'Manage web accounts, roles and privileges.',
    auditPageSubtitle: 'Complete log of changes across the system.',
    roleAdmin: 'Administrator',
    roleUser: 'User',
    roleAdminDesc: 'Full access including marking orders complete and user management.',
    roleUserDesc: 'Can work with orders, planning and teams. Cannot mark complete, reopen or delete orders.',
    addUser: 'Add user',
    editUser: 'Edit user',
    deleteUser: 'Delete user',
    userName: 'Name',
    userEmail: 'Email',
    userRole: 'Role',
    userPassword: 'Password',
    userPasswordOptional: 'New password (leave blank to keep)',
    userCreatedSuccess: 'User created.',
    userUpdatedSuccess: 'User updated.',
    userDeletedSuccess: 'User deleted.',
    userCreateFailed: 'Could not create user.',
    userUpdateFailed: 'Could not update user.',
    userDeleteFailed: 'Could not delete user.',
    confirmDeleteUser: 'Delete this user? This cannot be undone.',
    privilegesTitle: 'Role privileges',
    markCompleteAdminOnly: 'Only administrators can mark orders complete.',
    auditActor: 'Actor',
    auditAction: 'Action',
    auditEntity: 'Entity',
    auditSummary: 'Summary',
    auditWhen: 'When',
    auditDetails: 'Details',
    auditFilterAction: 'Filter by action',
    auditFilterAll: 'All actions',
    auditSearchPlaceholder: 'Search summary, actor, entity…',
    auditNoEntries: 'No audit entries yet.',
    auditTotal: (n) => (n === 1 ? '1 entry' : `${n} entries`),
    settings: 'Settings',
    logout: 'Logout',
    planningSystemLabel: 'Planning System',
    mainMenu: 'Main Menu',
    
    // Settings page
    settingsDescription: 'Customize your preferences',
    language: 'Language',
    languageDescription: 'Select your preferred language',
    appearance: 'Appearance',
    appearanceDescription: 'Choose your color scheme',
    appVersion: 'Version',
    lightTheme: 'Light',
    darkTheme: 'Dark',
    systemTheme: 'System',
    
    // Login
    welcomeBack: 'Welcome back',
    enterCredentials: 'Enter your credentials to continue',
    loginHeroLine1: 'Plan. Deliver.',
    loginHeroLine2: 'Excel.',
    loginHeroSubtitle: 'Streamline your delivery operations with our intelligent planning system.',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'admin@vmk.nl',
    passwordPlaceholder: '••••••••',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    
    // Offers page
    acceptedOffers: 'Accepted offers from Moneybird',
    total: 'total',
    searchOffers: 'Search by client, email, postcode, phone, address, offer #…',
    filter: 'Filter',
    offerSortLabel: 'Sort offers',
    offerSortAcceptedRecent: 'Most recently accepted',
    offerSortAcceptedOldest: 'Oldest accepted first',
    offerSortAmountHigh: 'Amount: highest to lowest',
    offerSortAmountLow: 'Amount: lowest to highest',
    offerSortClientAZ: 'Client name: A–Z',
    offerSortClientZA: 'Client name: Z–A',
    offerSortOfferNumber: 'Offer number',
    offerNumber: 'Offer #',
    client: 'Client',
    address: 'Address',
    contact: 'Contact',
    amount: 'Amount',
    status: 'Status',
    source: 'Source',
    sourceMoneybird: 'Moneybird',
    sourceGhl: 'GHL',
    createOrder: 'Create Order',
    manualOrder: 'New order',
    manualOrderFormTitle: 'Work preparation — new order',
    manualOrderSection: 'Project details',
    manualOrderHint: 'Create an order without an imported offer. Enter the client and project details below.',
    manualOfferReference: 'Project reference (optional)',
    manualOfferReferencePlaceholder: 'e.g. 2024-001 or project name',
    noOffersFound: 'No offers found matching your search.',
    failedLoadOfferDetails: 'Failed to load offer details',
    failedFetchOffers: 'Failed to fetch offers',
    offersSyncAddedOne: '1 new offer added',
    offersSyncAddedMany: '{count} new offers added',
    offersRefresh: 'Refresh offers',
    offersRefreshUpToDate: 'Offers are up to date',
    offersRefreshFailed: 'Could not refresh offers from Moneybird/GHL',
    offersLastUpdated: 'Last updated',
    failedCopyOfferId: 'Failed to copy offer ID',
    offerIdCopiedWithValue: 'Copied',
    moneybirdAuthRedirect: 'Moneybird authorization required. Redirecting…',
    
    // Form
    werkvoorbereidingForm: 'Work preparation form',
    saveChanges: 'Save changes',
    clientInformation: 'Client Information',
    companySection: 'Company',
    companyName: 'Company Name',
    monteurs: 'Monteurs / Mechanics',
    monteursPlaceholder: 'Enter monteur/mechanic names...',
    monteursTeamHint: 'Pick a team from the list to assign the order, or enter a custom name for manual assignment.',
    monteursPickTrigger: 'Select a team or enter a custom name…',
    monteursSearchPlaceholder: 'Search teams by name…',
    monteursRegisteredTeams: 'Registered teams',
    monteursQuickCustom: 'Custom name',
    monteursUseTypedName: 'Use this as monteur name (manual assignment)',
    monteursClearSelection: 'Clear selection',
    monteursNoMatches: 'No team matches this search.',
    monteursApplyManual: 'Use as manual monteur name',
    monteursLinkedTeam: 'This name matches a planning team — the order can be assigned to that team when you save.',
    monteursManualOnly: 'Custom text — no automatic team assignment.',
    teamsAvailable: 'teams',
    customCompanyPlaceholder: 'Enter company name...',
    customCompanyOption: 'Other (enter custom name)...',
    klant: 'Client',
    telefoonnummer: 'Phone Number',
    adres: 'Address',
    emailLabel: 'E-mail',
    postcodeWoonplaats: 'Postcode / City',
    kleur: 'Color',
    kozijnBuitenzijde: 'Frame Exterior',
    draaidealBuitenzijde: 'Sash Exterior',
    kozijnBinnenzijde: 'Frame Interior',
    folie: 'Foil',
    glasbokLabel: 'Glasbok required photo',
    dates: 'Dates',
    verkoopdatum: 'Sale Date',
    inmeetdatum: 'Measure Date',
    montagedatum: 'Assembly Date',
    montagedagen: 'Assembly Days',
    bestellingen: 'Orders',
    kozijnen: 'Frames (Kozijnen)',
    besteldatum: 'Order Date',
    leverdatum: 'Delivery Date',
    glas: 'Glass',
    deurvulling: 'Door infill',
    rooster: 'Grille',
    horren: 'Screens',
    keralit: 'Keralit',
    binnenafwerking: 'Interior Finish',
    vensterbanken: 'Window Sills',
    dakramenBmn: 'Skylights BMN',
    plastisol: 'Plastisol',
    raamdorpelstenen: 'Sill Stones',
    stelpakket: 'Shim Kit',
    rolluikenScreens: 'Shutters/Screens',
    sandwich: 'Sandwich Panels',
    container: 'Container',
    diversen: 'Miscellaneous',
    notes: 'Notes',
    additionalNotes: 'Additional notes...',
    cancel: 'Cancel',
    saveOrder: 'Save Order',
    orderCreateFailed: 'Failed to create order',
    orderUpdateFailed: 'Failed to update order',
    miscellaneousCombinedPlaceholder: 'Enter miscellaneous notes/materials in one field...',
    saving: 'Saving...',
    backToOrders: 'Back to Orders',
    printWerkvoorbereiding: 'Print preparation',
    
    // Orders page
    manageOrders: 'Manage and assign delivery orders',
    noOrdersYet: 'No orders yet',
    createOrdersFromOffers: 'Create orders from the Offers page to get started.',
    searchOrders: 'Search by client, email, postcode, phone, address, offer…',
    team: 'Team',
    scheduled: 'Scheduled',
    assign: 'Assign',
    unassign: 'Unassign',
    assignOrder: 'Assign Order',
    selectTeam: 'Select a team',
    scheduledDate: 'Scheduled Date',
    viewOrder: 'View',
    editOrder: 'Edit',
    viewPrintOrderTitle: 'View and print',
    editOrderTitleAttr: 'Edit order',
    backToOrdersList: 'Back to orders',
    editOrderPageTitle: 'Edit order',
    editOrderPageSubtitle: 'Update all preparation fields for this order.',
    selectTeamError: 'Please select a team',
    orderUpdatedSuccess: 'Order updated successfully',
    orderCreatedSuccess: 'Order created successfully',
    failedFetchData: 'Failed to fetch data',
    failedAssignOrder: 'Failed to assign order',
    failedUnassignOrder: 'Failed to unassign order',
    failedDeleteOrder: 'Failed to delete order',
    confirmDeleteOrder: 'Are you sure you want to delete this order?',
    orderDeletedSuccess: 'Order deleted',
    orderAssignedSuccess: 'Order assigned successfully',
    orderUnassignedSuccess: 'Order unassigned',
    selectedTeamNeedsMember: 'Selected team must have at least 1 member',
    failedSetPlannerColor: 'Failed to set planner color',
    track: 'Track',
    statusCompleted: 'completed',
    workflowInProgress: 'In progress',
    orderStatusAwaitingBalance: 'Awaiting confirmation',
    markOrderCompleteAfterBalance: 'Mark complete',
    confirmFinalizeOrderAfterBalance:
      'Mark this order complete and move it to Closed orders? The monteur finished on site — confirm in the office first.',
    orderFinalizedSuccess: 'Order completed and moved to Closed orders.',
    orderFinalizeFailed: 'Could not finalize order.',
    photoRemoveTitle: 'Remove photo?',
    photoRemoveMessage: 'Are you sure you want to remove this photo?',
    photoRemoveConfirm: 'Remove',
    photoRemoveCancel: 'Cancel',
    photoRemoved: 'Photo removed',
    photoDeleteFailed: 'Could not remove photo',
    glasbokPhotoRequiredError: 'Glasbok photo is required before completing this order.',
    pendingWorkLabel: 'Remaining work to be completed',
    requiredMaterialsLabel: 'Required materials',
    financialAmountLabel: 'Agreed amount to withhold',
    completionSlipLabel: 'Completion slip',
    tabNotifyOpleverSigned: 'Opleverbon signed on mobile',
    tabNotifyServicebonSigned: 'Servicebon signed on mobile',
    tabNotifyServicebonNeedWebText:
      'Servicebon: enter work carried out and save, then sign on mobile',
    tabNotifyOfficeBalance: 'Action: confirm balance — mark complete for Closed orders',
    tabNotifyWorkerNote: 'New note from mobile',
    navClosedOrdersAttentionTitle: 'Closed orders with unread mobile notes',
    navOrdersAttentionTitle:
      'One or more active orders need attention (signatures, servicebon text, or balance release)',
    servicebonLabel: 'Servicebon',
    servicebonWorkDraftTitle: 'Servicebon — work carried out (edit here)',
    servicebonWorkDraftEdit: 'Edit',
    servicebonWorkDraftHint:
      'Type here, then Save. The monteur sees this text on the phone before the customer signs.',
    servicebonWorkDraftSave: 'Save text',
    servicebonWorkDraftSaved: 'Servicebon text saved',
    servicebonWorkDraftFailed: 'Could not save servicebon text',
    servicebonVoiceStart: 'Start voice input',
    servicebonVoiceStop: 'Stop voice input',
    servicebonVoiceListening: 'Listening… speak now',
    servicebonVoiceMicDenied: 'Microphone access was denied',
    servicebonVoiceFailed: 'Voice input could not be started',
    servicebonVoiceAuthFailed: 'Please log in again to use voice input',
    servicebonNavAria: 'Choose which service form to view or print',
    servicebonNavTitle: 'Service forms',
    servicebonNavCurrent: 'Current',
    servicebonNavArchive: 'Archive',
    servicebonNavSignedShort: 'Signed on mobile',
    servicebonNavDraftShort: 'Draft / open round',
    servicebonArchiveRibbonPrefix: 'Archived form',
    servicebonCurrentRibbonPrefix: 'Current form',
    signatureDeclaration:
      'Dear customer,\n\nThis report clarifies to what extent we as supplier have met our obligations as agreed in the Technical Report. Only by describing below what was not completed can full clarity be created. The supplier commits to complete the remaining work listed below within the agreed term. By signing, you declare that all products ordered by you have been delivered properly and that all installation-related work has been carried out correctly, except for any remaining work explicitly listed below. Any agreements made between you and our installer must be written in this report if you want to rely on those agreements.',
    financialSettlementInfo:
      'Financial settlement:\nYou can agree with the installer to withhold an agreed amount from the final payment term until the remaining work listed above is completed, including on the invoice.',
    signedByLabel: 'Signed by',
    opleverbonClientName: 'Client name',
    opleverbonAddress: 'Address',
    opleverbonPcPlaats: 'Postcode / City',
    opleverbonPhoneResident: 'Phone',
    opleverbonInstallers: 'Installers',
    opleverbonDate: 'Date',
    opleverbonPlace: 'Place',
    opleverbonAgreement: 'Approved',
    opleverbonCustomerName: 'Customer name',
    opleverbonClientSignature: 'Customer signature',
    opleverbonInvoiceHold: 'Withholding on invoice',
    opleverbonInvoiceHoldNee: 'No',
    opleverbonInvoiceHoldJa10: 'Yes 10%',
    opleverbonInvoiceHoldJaPrefix: 'Yes',
    opleverbonInvoiceHoldPctMax: 'max. 20%',
    voorbladWerkvoorbereiding: 'Preparation cover sheet',
    printLabel: 'Print',
    notesLabel: 'Notes',
    photosLabel: 'Photos',
    filesLabel: 'Files',
    filesLoading: 'Loading…',
    filesAddFolder: 'Add folder',
    filesDeleteFolder: 'Delete folder',
    filesUpload: 'Upload file',
    filesEmail: 'Email link',
    filesPrint: 'Print',
    filesRenameFolder: 'Rename folder',
    filesOpen: 'Open',
    filesDeleteFile: 'Delete file',
    filesLoadFailed: 'Could not load files',
    filesFolderNamePrompt: 'Folder name',
    filesFolderNameRequired: 'Enter a folder name',
    filesFolderCreated: 'Folder created',
    filesFolderCreateFailed: 'Could not create folder',
    filesSelectFolderFirst: 'Select a folder first',
    filesCannotDeleteDefault: 'Default folders cannot be deleted',
    filesConfirmDeleteFolder: 'Delete this folder and everything inside it?',
    filesFolderDeleted: 'Folder deleted',
    filesFolderDeleteFailed: 'Could not delete folder',
    filesRenameFolderPrompt: 'New folder name',
    filesFolderRenamed: 'Folder renamed',
    filesFolderRenameFailed: 'Could not rename folder',
    filesSelectFolderForUpload: 'Select a folder (or a file inside a folder) to upload into that folder',
    filesUploadSuccess: 'File uploaded',
    filesUploadMultipleSuccess: '{count} files uploaded',
    filesUploadFailed: 'Upload failed',
    filesSelectFileForAction: 'Select a file or mobile photo first',
    filesConfirmDeleteFile: 'Delete this file?',
    filesFileDeleted: 'File deleted',
    filesFileDeleteFailed: 'Could not delete file',
    filesEmptyTree: 'No folders yet.',
    filesPhotoBefore: 'Before',
    filesPhotoDuring: 'During',
    filesPhotoAfter: 'After',
    filesPhotoGlasbok: 'Glasbok',
    filesPhotoGeneric: 'Photo',
    filesNetworkLabel: 'Network folder',
    filesNetworkNotConfigured: 'Network folder is not configured on the server.',
    filesNetworkUnavailable: 'Network folder is not reachable right now.',
    filesNetworkLoadFailed: 'Could not load network folder',
    filesNetworkEmpty: 'This folder is empty.',
    filesRefresh: 'Refresh',
    filesDownload: 'Download',
    filesNetworkImported: 'File copied to order files',
    filesNetworkImportFailed: 'Could not copy file to order files',
    filesNetworkDragHint: 'Drag files onto an order folder on the left to copy them',
    filesNetworkSyncedBadge: 'Synced',
    filesNetworkLastSync: 'Last sync',
    filesNetworkWaitingSyncTitle: 'Waiting for office sync',
    filesNetworkWaitingSyncBody:
      'The online server cannot reach your office share directly. Run the network sync agent on any office PC with access to Z:\\ or \\\\MARTIJN. The first run uploads all folders; later runs add only new folders.',
    filesNetworkBrowserSyncHint:
      'Choose your shared folder once (e.g. Z:\\ or VKMMARTIJN-database files). Click Sync to upload new folders to the online server. No extra software needed — use Chrome or Edge.',
    filesNetworkChooseFolder: 'Choose folder',
    filesNetworkChangeFolder: 'Change folder',
    filesNetworkSyncNow: 'Sync now',
    filesNetworkSyncing: 'Syncing…',
    filesNetworkAutoSync: 'Sync automatically when I open Files',
    filesNetworkFolderConnected: 'Connected: {name}',
    filesNetworkConnectFailed: 'Could not connect folder',
    filesNetworkPermissionDenied: 'Permission to read the folder was denied',
    filesNetworkUnsupportedBrowser: 'Use Chrome or Edge to connect a shared folder from this PC',
    filesNetworkSyncFailed: 'Sync failed',
    filesNetworkSyncDone: '{count} files uploaded',
    filesNetworkSyncUpToDate: 'Already up to date — no new folders to upload',
    filesNetworkSyncChecking: 'Checking which folders are new…',
    filesNetworkSyncFolder: 'Uploading folder: {name}',
    filesNetworkSyncFile: '{folder}: file {current} of {total}',
    filesNetworkEmptyChooseFolder: 'Choose your shared folder above, then click Sync now.',
    filesNetworkLiveConnected:
      'This PC is connected directly to the network folder (Z:). Folders show live — no sync needed here. On the online website, use Choose folder and Sync now.',
    networkFolderTitle: 'Network folder',
    networkFolderDescription: 'Shared folder shown in the Files tab of every order',
    networkFolderPathLabel: 'Folder path',
    networkFolderPathHint:
      'Path as seen by the server, e.g. Z:\\ or \\\\MARTIJN\\VKMMARTIJN-database files. The server must have access to this folder.',
    networkFolderLabelLabel: 'Display name (optional)',
    networkFolderSave: 'Save',
    networkFolderSaved: 'Network folder saved',
    networkFolderSavedUnreachable: 'Saved, but the folder is not reachable from the server',
    networkFolderSaveFailed: 'Could not save network folder',
    networkFolderStatusOk: 'Connected',
    networkFolderStatusUnreachable: 'Not reachable',
    networkFolderStatusUnconfigured: 'Not configured',
    networkFolderSyncTitle: 'Office sync (online server)',
    networkFolderSyncDescription:
      'On Hostinger the API cannot read Z:\\ directly. In the Files tab, staff click Choose folder (Chrome/Edge), select the shared drive once, then Sync now. The folder is remembered on that PC. Optional: advanced users can still use backend/scripts/network_sync_agent.py.',
    statusLabel: 'Status',
    plannerNotesLabel: 'Planner notes',
    noPlannerNote: 'No planner note.',
    workerNotesLabel: 'Worker notes',
    noWorkerNotesYet: 'No worker notes yet.',
    noPhotosUploaded: 'No photos uploaded.',
    workCarriedOutLabel: 'Work carried out:',
    remainingWorkLabel: 'Remaining work / open items:',
    requiredMaterialsInfo:
      'Please indicate below which materials are already available, which still need to be ordered, which can be reused, and whether additional support materials are needed.',
    financialSettlementLabel: 'Financial settlement:',
    coverSheetTitle: 'Preparation cover sheet',
    clientLabel: 'Client:',
    addressLabel: 'Address:',
    postcodeCityLabel: 'Postcode / city',
    phoneLabel: 'Phone',
    emailLabel: 'E-mail',
    colorSectionLabel: 'Color',
    exteriorFrameLabel: 'Exterior frame',
    exteriorSashLabel: 'Exterior sash',
    interiorFrameLabel: 'Interior frame',
    foilLabel: 'Foil:',
    yesLabel: 'yes',
    noLabel: 'no',
    salesDateLabel: 'Sales date',
    installDateLabel: 'Installation date',
    measureDateLabel: 'Measurement date',
    installDaysLabel: 'Installation days',
    ordersHeaderLabel: 'Orders',
    orderedDateHeader: 'Order date',
    deliveryDateHeader: 'Delivery date',
    installersLabel: 'Installers:',

    // Teams page
    teamsAndWorkers: 'Teams & Workers',
    manageTeams: 'Manage your delivery teams and workers',
    addWorker: 'Add Worker',
    addTeam: 'Add Team',
    noTeamsYet: 'No teams yet',
    noWorkersYet: 'No workers yet',
    addNewTeam: 'Add New Team',
    teamName: 'Team Name',
    type: 'Type',
    individualWorker: 'Individual Worker',
    teamGroup: 'Team Group',
    teamColor: 'Team Color',
    members: 'Members',
    createTeam: 'Create Team',
    addNewWorker: 'Add New Worker',
    name: 'Name',
    phone: 'Phone',
    noContactInfo: 'No contact info',
    workers: 'Workers',
    prev: 'Prev',
    next: 'Next',
    rowsPerPage: 'Rows per page',
    page: 'Page',
    page: 'Page',
    teamNameRequired: 'Team name is required',
    teamCreatedSuccess: 'Team created successfully',
    teamCreateFailed: 'Failed to create team',
    teamUpdatedSuccess: 'Team updated successfully',
    teamUpdateFailed: 'Failed to update team',
    teamDeletedSuccess: 'Team deleted',
    teamDeleteFailed: 'Failed to delete team',
    workerNameRequired: 'Worker name is required',
    workerAddedSuccess: 'Worker added successfully',
    workerAddFailed: 'Failed to add worker',
    workerUpdatedSuccess: 'Worker updated successfully',
    workerUpdateFailed: 'Failed to update worker',
    workerDeletedSuccess: 'Worker deleted',
    workerDeleteFailed: 'Failed to delete worker',
    confirmDeleteTeam: 'Are you sure you want to delete this team?',
    confirmDeleteWorker: 'Are you sure you want to delete this worker?',
    teamNamePlaceholder: 'e.g., Team Alpha',
    workerNamePlaceholder: 'Worker name',
    workerPhonePlaceholder: '06-12345678',
    workerEmailPlaceholder: 'worker@email.com',
    editTeam: 'Edit team',
    editWorker: 'Edit worker',
    
    // Planning page
    week: 'Week',
    today: 'Today',
    unassigned: 'Unassigned',
    allOrdersAssigned: 'All orders assigned!',
    addTeamsFirst: 'Add teams first',
    noTeamsConfigured: 'No teams configured yet. Add teams in the Teams page to start planning deliveries.',
    planningWeekTotalLabel: 'Week total (planned orders)',
    planningMonthTotalLabel: 'Month total (planned orders)',
    planningTotalExcludesMissing: 'excl. orders without stored amount',
    planningSearchPlaceholder: 'Search by client, email, postcode, phone, address…',
    planningSearchNoResults: 'No planned order found in this period.',
    planningWorkday: 'workday',
    planningWorkdays: 'workdays',
    planningDragTeamRequired: 'Please drag to a specific team column',
    planningServiceQueue: 'Service',
    planningServiceAwaiting: 'Awaiting schedule',
    planningServiceClearedForReschedule: 'Service schedule cleared — drag to a new day to replan.',
    planningWorkDaysUpdated: 'Work days updated',
    planningWorkDaysUpdateFailed: 'Could not update work days',
    planningNoSidebarOrders: 'No orders waiting to be planned.',
    planningSidebarDays: 'Work days',
    month: 'Month',
    more: 'more',

    // Days
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    
    // Stats
    deliveriesMonth: 'Deliveries/Month',
    activeTeams: 'Active Teams',
    onTimeRate: 'On-Time Rate',

    // Offer detail (full page)
    backToOffers: 'Back to offers',
    offerIdLabel: 'Offer ID',
    offerCreated: 'Created',
    offerAccepted: 'Accepted',
    offerClientDetails: 'Client details',
    offerContactDetails: 'Contact details',
    offerCopied: 'Offer ID copied',
    offerCopyFailed: 'Could not copy offer ID',
  },
  nl: {
    // Navigation
    offers: 'Offertes',
    orders: 'Orders in Uitvoering',
    closedOrders: 'Afgesloten orders',
    historyView: 'Bekijken',
    historyEdit: 'Bewerken',
    historyRevoke: 'Heropenen',
    completedAt: 'Afgerond op',
    actions: 'Acties',
    reopenOrderConfirm:
      'Order heropenen? De order komt weer in de actieve lijst (orders en offertes).',
    reopenOrderSuccess: 'Order heropend; de order is weer actief.',
    reopenOrderFailed: 'Order kon niet worden heropend',
    teams: 'Teams',
    planning: 'Planning',
    service: 'Service',
    servicePageSubtitle:
      'Serviceorders na restpunten op de opleverbon of na heropenen van een afgesloten order.',
    serviceActiveSection: 'Actieve service',
    serviceArchiveSection: 'Afgeronde service',
    serviceStatusColumn: 'Servicestatus',
    serviceRoundsColumn: 'Service-rondes',
    serviceRoundCount: (n) => (n === 1 ? '1 ronde' : `${n} rondes`),
    serviceOpenServicebon: 'Servicebon',
    serviceNoActive: 'Geen actieve serviceorders.',
    serviceNoArchive: 'Geen afgeronde orders met servicebon-historie.',
    serviceStatusAwaitingSign: 'Wacht op servicebon',
    serviceStatusInProgress: 'Service bezig',
    serviceStatusShortNeedText: 'Open',
    serviceStatusShortSigned: 'Getekend op mobiel',
    serviceStatusShortAwaiting: 'Wacht op servicebon',
    serviceStatusShortProgress: 'Service bezig',
    navServiceAttentionTitle: 'Actieve serviceorders vragen aandacht',
    users: 'Gebruikers',
    auditTrail: 'Auditlog',
    adminSection: 'Beheer',
    adminOnlyPage: 'Alleen voor beheerders',
    usersPageSubtitle: 'Beheer webaccounts, rollen en rechten.',
    auditPageSubtitle: 'Volledig logboek van wijzigingen in het systeem.',
    roleAdmin: 'Beheerder',
    roleUser: 'Gebruiker',
    roleAdminDesc: 'Volledige toegang, incl. orders afsluiten en gebruikersbeheer.',
    roleUserDesc: 'Kan werken met orders, planning en teams. Kan orders niet afsluiten, heropenen of verwijderen.',
    addUser: 'Gebruiker toevoegen',
    editUser: 'Gebruiker bewerken',
    deleteUser: 'Gebruiker verwijderen',
    userName: 'Naam',
    userEmail: 'E-mail',
    userRole: 'Rol',
    userPassword: 'Wachtwoord',
    userPasswordOptional: 'Nieuw wachtwoord (leeg = ongewijzigd)',
    userCreatedSuccess: 'Gebruiker aangemaakt.',
    userUpdatedSuccess: 'Gebruiker bijgewerkt.',
    userDeletedSuccess: 'Gebruiker verwijderd.',
    userCreateFailed: 'Gebruiker kon niet worden aangemaakt.',
    userUpdateFailed: 'Gebruiker kon niet worden bijgewerkt.',
    userDeleteFailed: 'Gebruiker kon niet worden verwijderd.',
    confirmDeleteUser: 'Deze gebruiker verwijderen? Dit kan niet ongedaan worden gemaakt.',
    privilegesTitle: 'Rolrechten',
    markCompleteAdminOnly: 'Alleen beheerders kunnen orders als afgerond markeren.',
    auditActor: 'Gebruiker',
    auditAction: 'Actie',
    auditEntity: 'Object',
    auditSummary: 'Samenvatting',
    auditWhen: 'Tijdstip',
    auditDetails: 'Details',
    auditFilterAction: 'Filter op actie',
    auditFilterAll: 'Alle acties',
    auditSearchPlaceholder: 'Zoek samenvatting, gebruiker, object…',
    auditNoEntries: 'Nog geen auditregels.',
    auditTotal: (n) => (n === 1 ? '1 regel' : `${n} regels`),
    settings: 'Instellingen',
    logout: 'Uitloggen',
    planningSystemLabel: 'Planningssysteem',
    mainMenu: 'Hoofdmenu',
    
    // Settings page
    settingsDescription: 'Pas uw voorkeuren aan',
    language: 'Taal',
    languageDescription: 'Selecteer uw voorkeurstaal',
    appearance: 'Weergave',
    appearanceDescription: 'Kies uw kleurenschema',
    appVersion: 'Versie',
    lightTheme: 'Licht',
    darkTheme: 'Donker',
    systemTheme: 'Systeem',
    
    // Login
    welcomeBack: 'Welkom terug',
    enterCredentials: 'Voer uw gegevens in om door te gaan',
    loginHeroLine1: 'Plan. Lever.',
    loginHeroLine2: 'Excelleren.',
    loginHeroSubtitle: 'Stroomlijn je leveringsproces met ons intelligente planningssysteem.',
    email: 'E-mail',
    password: 'Wachtwoord',
    emailPlaceholder: 'admin@vmk.nl',
    passwordPlaceholder: '••••••••',
    signIn: 'Inloggen',
    signingIn: 'Bezig met inloggen...',
    
    // Offers page
    acceptedOffers: 'Geaccepteerde offertes van Moneybird',
    total: 'totaal',
    searchOffers: 'Zoek op klant, e-mail, postcode, tel., adres, offert nr.…',
    filter: 'Filter',
    offerSortLabel: 'Offertes sorteren',
    offerSortAcceptedRecent: 'Recent geaccepteerd',
    offerSortAcceptedOldest: 'Eerst geaccepteerd (oudste eerst)',
    offerSortAmountHigh: 'Bedrag: hoog naar laag',
    offerSortAmountLow: 'Bedrag: laag naar hoog',
    offerSortClientAZ: 'Klantnaam: A–Z',
    offerSortClientZA: 'Klantnaam: Z–A',
    offerSortOfferNumber: 'Offertenummer',
    offerNumber: 'Offerte #',
    client: 'Klant',
    address: 'Adres',
    contact: 'Contact',
    amount: 'Bedrag',
    status: 'Status',
    source: 'Bron',
    sourceMoneybird: 'Moneybird',
    sourceGhl: 'GHL',
    createOrder: 'Bestelling maken',
    manualOrder: 'Nieuwe bestelling',
    manualOrderFormTitle: 'Werkvoorbereiding — nieuwe bestelling',
    manualOrderSection: 'Projectgegevens',
    manualOrderHint: 'Maak een bestelling aan zonder geïmporteerde offerte. Vul hieronder de klant- en projectgegevens in.',
    manualOfferReference: 'Projectreferentie (optioneel)',
    manualOfferReferencePlaceholder: 'bijv. 2024-001 of projectnaam',
    noOffersFound: 'Geen offertes gevonden die overeenkomen met uw zoekopdracht.',
    failedLoadOfferDetails: 'Offertegegevens laden mislukt',
    failedFetchOffers: 'Offertes ophalen mislukt',
    offersSyncAddedOne: '1 nieuwe offerte toegevoegd',
    offersSyncAddedMany: '{count} nieuwe offertes toegevoegd',
    offersRefresh: 'Offertes vernieuwen',
    offersRefreshUpToDate: 'Offertes zijn bijgewerkt (geen nieuwe)',
    offersRefreshFailed: 'Offertes vernieuwen mislukt (Moneybird/GHL)',
    offersLastUpdated: 'Laatst bijgewerkt',
    failedCopyOfferId: 'Offerte-ID kopiëren mislukt',
    offerIdCopiedWithValue: 'Gekopieerd',
    moneybirdAuthRedirect: 'Moneybird-autorisatie vereist. Doorsturen…',
    
    // Form
    werkvoorbereidingForm: 'Werkvoorbereiding formulier',
    saveChanges: 'Wijzigingen opslaan',
    clientInformation: 'Klantgegevens',
    companySection: 'Bedrijf',
    companyName: 'Bedrijfsnaam',
    monteurs: 'Monteurs',
    monteursPlaceholder: 'Voer monteursnamen in...',
    monteursTeamHint: 'Kies een team uit de lijst om de order toe te wijzen, of vul een eigen naam in voor handmatige toewijzing.',
    monteursPickTrigger: 'Selecteer een team of vul een eigen naam in…',
    monteursSearchPlaceholder: 'Zoek teams op naam…',
    monteursRegisteredTeams: 'Geregistreerde teams',
    monteursQuickCustom: 'Eigen naam',
    monteursUseTypedName: 'Gebruik dit als monteursnaam (handmatige toewijzing)',
    monteursClearSelection: 'Wis selectie',
    monteursNoMatches: 'Geen team gevonden voor deze zoekopdracht.',
    monteursApplyManual: 'Gebruik als handmatige monteursnaam',
    monteursLinkedTeam: 'Deze naam hoort bij een planningsteam — de order kan aan dat team worden toegekend bij opslaan.',
    monteursManualOnly: 'Eigen tekst — geen automatische teamtoewijzing.',
    teamsAvailable: 'teams',
    customCompanyPlaceholder: 'Voer bedrijfsnaam in...',
    customCompanyOption: 'Anders (eigen naam invullen)...',
    klant: 'Klant',
    telefoonnummer: 'Telefoonnummer',
    adres: 'Adres',
    emailLabel: 'E-mail',
    postcodeWoonplaats: 'Postcode/Woonplaats',
    kleur: 'Kleur',
    kozijnBuitenzijde: 'Kozijn Buitenzijde',
    draaidealBuitenzijde: 'Draaideel Buitenzijde',
    kozijnBinnenzijde: 'Kozijn Binnenzijde',
    folie: 'Folie',
    glasbokLabel: 'Glasbok foto verplicht',
    dates: 'Data',
    verkoopdatum: 'Verkoopdatum',
    inmeetdatum: 'Inmeetdatum',
    montagedatum: 'Montagedatum',
    montagedagen: 'Montagedagen',
    bestellingen: 'Bestellingen',
    kozijnen: 'Kozijnen',
    besteldatum: 'Besteldatum',
    leverdatum: 'Leverdatum',
    glas: 'Glas',
    deurvulling: 'Deurvulling',
    rooster: 'Rooster',
    horren: 'Horren',
    keralit: 'Keralit',
    binnenafwerking: 'Binnenafwerking',
    vensterbanken: 'Vensterbanken',
    dakramenBmn: 'Dakramen BMN',
    plastisol: 'Plastisol',
    raamdorpelstenen: 'Raamdorpelstenen',
    stelpakket: 'Stelpakket',
    rolluikenScreens: 'Rolluiken/Screens',
    sandwich: 'Sandwich',
    container: 'Container',
    diversen: 'Diversen',
    notes: 'Notities',
    additionalNotes: 'Extra notities...',
    cancel: 'Annuleren',
    saveOrder: 'Bestelling opslaan',
    orderCreateFailed: 'Bestelling aanmaken mislukt',
    orderUpdateFailed: 'Bestelling bijwerken mislukt',
    miscellaneousCombinedPlaceholder: 'Vul diverse opmerkingen/materialen in in een enkel veld...',
    saving: 'Opslaan...',
    backToOrders: 'Terug naar Bestellingen',
    printWerkvoorbereiding: 'Print werkvoorbereiding',
    
    // Orders page
    manageOrders: 'Beheer en wijs leveringsorders toe',
    noOrdersYet: 'Nog geen bestellingen',
    createOrdersFromOffers: 'Maak bestellingen aan vanaf de Offertes pagina om te beginnen.',
    searchOrders: 'Zoek op klant, e-mail, postcode, tel., adres, aanbod…',
    team: 'Team',
    scheduled: 'Gepland',
    assign: 'Toewijzen',
    unassign: 'Intrekken',
    assignOrder: 'Bestelling toewijzen',
    selectTeam: 'Selecteer een team',
    scheduledDate: 'Geplande datum',
    viewOrder: 'Bekijken',
    editOrder: 'Bewerken',
    viewPrintOrderTitle: 'Bekijk en print',
    editOrderTitleAttr: 'Bestelling bewerken',
    backToOrdersList: 'Terug naar bestellingen',
    editOrderPageTitle: 'Bestelling bewerken',
    editOrderPageSubtitle: 'Werk alle voorbereidingsvelden voor deze bestelling bij.',
    selectTeamError: 'Selecteer een team',
    orderUpdatedSuccess: 'Bestelling bijgewerkt',
    orderCreatedSuccess: 'Bestelling aangemaakt',
    failedFetchData: 'Gegevens ophalen mislukt',
    failedAssignOrder: 'Toewijzen mislukt',
    failedUnassignOrder: 'Intrekken mislukt',
    failedDeleteOrder: 'Verwijderen mislukt',
    confirmDeleteOrder: 'Weet je zeker dat je deze bestelling wilt verwijderen?',
    orderDeletedSuccess: 'Bestelling verwijderd',
    orderAssignedSuccess: 'Bestelling succesvol toegewezen',
    orderUnassignedSuccess: 'Bestelling niet meer toegewezen',
    selectedTeamNeedsMember: 'Geselecteerd team moet minimaal 1 lid hebben',
    failedSetPlannerColor: 'Planner-kleur instellen mislukt',
    track: 'Volgen',
    statusCompleted: 'afgerond',
    workflowInProgress: 'In uitvoering',
    orderStatusAwaitingBalance: 'Wacht op bevestiging',
    markOrderCompleteAfterBalance: 'Markeer als afgerond',
    confirmFinalizeOrderAfterBalance:
      'Deze order als afgerond markeren en naar Gesloten bestellingen verplaatsen? De monteur is klaar op locatie — eerst op kantoor bevestigen.',
    orderFinalizedSuccess: 'Order afgerond en verplaatst naar Gesloten bestellingen.',
    orderFinalizeFailed: 'Order afronden mislukt.',
    photoRemoveTitle: 'Foto verwijderen?',
    photoRemoveMessage: 'Weet je zeker dat je deze foto wilt verwijderen?',
    photoRemoveConfirm: 'Verwijderen',
    photoRemoveCancel: 'Annuleren',
    photoRemoved: 'Foto verwijderd',
    photoDeleteFailed: 'Foto niet verwijderd',
    glasbokPhotoRequiredError: 'Glasbok foto is verplicht voordat deze order kan worden afgerond.',
    pendingWorkLabel: 'Nog uit te voeren werkzaamheden',
    requiredMaterialsLabel: 'Benodigde materialen',
    financialAmountLabel: 'Overeengekomen bedrag om in te houden',
    completionSlipLabel: 'Opleverbon',
    tabNotifyOpleverSigned: 'Opleverbon getekend op mobiel',
    tabNotifyServicebonSigned: 'Servicebon getekend op mobiel',
    tabNotifyServicebonNeedWebText:
      'Servicebon: vul uitgevoerde werkzaamheden in en sla op, daarna tekenen op mobiel',
    tabNotifyOfficeBalance: 'Actie: saldo bevestigen — markeer afgerond voor gesloten orders',
    tabNotifyWorkerNote: 'Nieuwe notitie van mobiel',
    navClosedOrdersAttentionTitle: 'Gesloten orders met ongelezen mobiele notities',
    navOrdersAttentionTitle:
      'Er zijn actieve orders die aandacht vragen (handtekeningen, servicebon-tekst of saldo)',
    servicebonLabel: 'Servicebon',
    servicebonWorkDraftTitle: 'Servicebon — uitgevoerde werkzaamheden (hier bewerken)',
    servicebonWorkDraftEdit: 'Bewerken',
    servicebonWorkDraftHint:
      'Typ hier en klik op Opslaan. Het montageteam ziet deze tekst op de telefoon vóór de klant tekent.',
    servicebonWorkDraftSave: 'Tekst opslaan',
    servicebonWorkDraftSaved: 'Servicebon-tekst opgeslagen',
    servicebonWorkDraftFailed: 'Servicebon-tekst opslaan mislukt',
    servicebonVoiceStart: 'Spraak invoeren starten',
    servicebonVoiceStop: 'Spraak invoeren stoppen',
    servicebonVoiceListening: 'Luisteren… spreek nu',
    servicebonVoiceMicDenied: 'Microfoontoegang geweigerd',
    servicebonVoiceFailed: 'Spraakinvoer kon niet worden gestart',
    servicebonVoiceAuthFailed: 'Log opnieuw in om spraakinvoer te gebruiken',
    servicebonNavAria: 'Kies welke servicebon u bekijkt of afdrukt',
    servicebonNavTitle: 'Servicebonnen',
    servicebonNavCurrent: 'Actueel',
    servicebonNavArchive: 'Archief',
    servicebonNavSignedShort: 'Getekend op mobiel',
    servicebonNavDraftShort: 'Concept / open ronde',
    servicebonArchiveRibbonPrefix: 'Gearchiveerde bon',
    servicebonCurrentRibbonPrefix: 'Actuele bon',
    signatureDeclaration:
      'Geachte heer mevrouw,\n\nDit rapport creeert duidelijkheid in hoeverre wij als leverancier aan verplichtingen, zoals overeengekomen op het Techisch Rapport hebben voldaan. Uitsluitend door hieronder te omschrijven waarin tekort is geschoten kan duidelijkheid worden gecreeerd. De leverancier verplicht zich jegens u als klant, onderstaande nog uit te voeren werkzaamheden alsnog, binnen de overeengekomen termijn in orde te maken. Door de ondertekening verklaart u dat alle, met uitzondering van eventueel hieronder vermelde (rest) werkzaamheden, door u bestelde producten deugdelijk zijn geleverd, dat alle met de plaatsing verbonden werkzaam heden naar behoren zijn uitgevoerd eb dat de leverancier daarmee aan alle overige op haar rustende verplichtingen heeft voldaan. Alle tussen u onze monteur gemaakte afspraken dienen op dit rapport vermeld te worden, wilt u zich op deze afspraken kunnen beroepen.',
    financialSettlementInfo:
      'Financieel afwikkeling:\nU kunt met de monteur een overeengekomen bedrag op de laatste termijn in houden totdat de hierboven vermelde (rest) werkzaamheden zijn uitgevoerd: inclusief op de factuur.',
    signedByLabel: 'Getekend door',
    opleverbonClientName: 'Naam klant',
    opleverbonAddress: 'Adres',
    opleverbonPcPlaats: 'PC/Plaats',
    opleverbonPhoneResident: 'Tel. bewoner',
    opleverbonInstallers: 'Monteurs',
    opleverbonDate: 'Datum',
    opleverbonPlace: 'Plaats',
    opleverbonAgreement: 'Voor akkoord',
    opleverbonCustomerName: 'Klantnaam',
    opleverbonClientSignature: 'Handtekening klant',
    opleverbonInvoiceHold: 'Inhouding op de factuur',
    opleverbonInvoiceHoldNee: 'Nee',
    opleverbonInvoiceHoldJa10: 'Ja 10%',
    opleverbonInvoiceHoldJaPrefix: 'Ja',
    opleverbonInvoiceHoldPctMax: 'max. 20%',
    voorbladWerkvoorbereiding: 'Voorblad Werkvoorbereiding',
    printLabel: 'Print',
    notesLabel: 'Notities',
    photosLabel: 'Foto\'s',
    filesLabel: 'Bestanden',
    filesLoading: 'Laden…',
    filesAddFolder: 'Map toevoegen',
    filesDeleteFolder: 'Map verwijderen',
    filesUpload: 'Bestand uploaden',
    filesEmail: 'E-mail link',
    filesPrint: 'Afdrukken',
    filesRenameFolder: 'Map hernoemen',
    filesOpen: 'Openen',
    filesDeleteFile: 'Bestand verwijderen',
    filesLoadFailed: 'Bestanden laden mislukt',
    filesFolderNamePrompt: 'Mapnaam',
    filesFolderNameRequired: 'Voer een mapnaam in',
    filesFolderCreated: 'Map aangemaakt',
    filesFolderCreateFailed: 'Map aanmaken mislukt',
    filesSelectFolderFirst: 'Selecteer eerst een map',
    filesCannotDeleteDefault: 'Standaardmappen kunnen niet worden verwijderd',
    filesConfirmDeleteFolder: 'Deze map en alles erin verwijderen?',
    filesFolderDeleted: 'Map verwijderd',
    filesFolderDeleteFailed: 'Map verwijderen mislukt',
    filesRenameFolderPrompt: 'Nieuwe mapnaam',
    filesFolderRenamed: 'Map hernoemd',
    filesFolderRenameFailed: 'Map hernoemen mislukt',
    filesSelectFolderForUpload: 'Selecteer een map (of een bestand in een map) om daar te uploaden',
    filesUploadSuccess: 'Bestand geüpload',
    filesUploadMultipleSuccess: '{count} bestanden geüpload',
    filesUploadFailed: 'Upload mislukt',
    filesSelectFileForAction: 'Selecteer eerst een bestand of mobiele foto',
    filesConfirmDeleteFile: 'Dit bestand verwijderen?',
    filesFileDeleted: 'Bestand verwijderd',
    filesFileDeleteFailed: 'Bestand verwijderen mislukt',
    filesEmptyTree: 'Nog geen mappen.',
    filesPhotoBefore: 'Voor',
    filesPhotoDuring: 'Tijdens',
    filesPhotoAfter: 'Na',
    filesPhotoGlasbok: 'Glasbok',
    filesPhotoGeneric: 'Foto',
    filesNetworkLabel: 'Netwerkmap',
    filesNetworkNotConfigured: 'Netwerkmap is niet geconfigureerd op de server.',
    filesNetworkUnavailable: 'Netwerkmap is momenteel niet bereikbaar.',
    filesNetworkLoadFailed: 'Netwerkmap laden mislukt',
    filesNetworkEmpty: 'Deze map is leeg.',
    filesRefresh: 'Vernieuwen',
    filesDownload: 'Downloaden',
    filesNetworkImported: 'Bestand gekopieerd naar orderbestanden',
    filesNetworkImportFailed: 'Bestand kopiëren naar orderbestanden mislukt',
    filesNetworkDragHint: 'Sleep bestanden naar een ordermap links om ze te kopiëren',
    filesNetworkSyncedBadge: 'Gesynchroniseerd',
    filesNetworkLastSync: 'Laatste sync',
    filesNetworkWaitingSyncTitle: 'Wachten op kantoor-sync',
    filesNetworkWaitingSyncBody:
      'De online server kan uw kantoor-share niet direct bereiken. Start de sync-agent op een kantoor-PC met toegang tot Z:\\ of \\\\MARTIJN. De eerste run uploadt alle mappen; daarna alleen nieuwe mappen.',
    filesNetworkBrowserSyncHint:
      'Kies uw gedeelde map één keer (bijv. Z:\\ of VKMMARTIJN-database files). Klik Sync om nieuwe mappen naar de online server te uploaden. Geen extra software — gebruik Chrome of Edge.',
    filesNetworkChooseFolder: 'Map kiezen',
    filesNetworkChangeFolder: 'Map wijzigen',
    filesNetworkSyncNow: 'Nu synchroniseren',
    filesNetworkSyncing: 'Bezig met sync…',
    filesNetworkAutoSync: 'Automatisch syncen bij openen Bestanden',
    filesNetworkFolderConnected: 'Verbonden: {name}',
    filesNetworkConnectFailed: 'Map koppelen mislukt',
    filesNetworkPermissionDenied: 'Geen toestemming om de map te lezen',
    filesNetworkUnsupportedBrowser: 'Gebruik Chrome of Edge om een gedeelde map vanaf deze PC te koppelen',
    filesNetworkSyncFailed: 'Sync mislukt',
    filesNetworkSyncDone: '{count} bestanden geüpload',
    filesNetworkSyncUpToDate: 'Al bijgewerkt — geen nieuwe mappen om te uploaden',
    filesNetworkSyncChecking: 'Controleren welke mappen nieuw zijn…',
    filesNetworkSyncFolder: 'Map uploaden: {name}',
    filesNetworkSyncFile: '{folder}: bestand {current} van {total}',
    filesNetworkEmptyChooseFolder: 'Kies hierboven uw gedeelde map en klik op Nu synchroniseren.',
    filesNetworkLiveConnected:
      'Deze PC is direct verbonden met de netwerkmap (Z:). Mappen worden live getoond — hier is geen sync nodig. Op de online website gebruikt u Map kiezen en Nu synchroniseren.',
    networkFolderTitle: 'Netwerkmap',
    networkFolderDescription: 'Gedeelde map die in het tabblad Bestanden van elke order wordt getoond',
    networkFolderPathLabel: 'Map-pad',
    networkFolderPathHint:
      'Pad zoals de server het ziet, bijv. Z:\\ of \\\\MARTIJN\\VKMMARTIJN-database files. De server moet toegang hebben tot deze map.',
    networkFolderLabelLabel: 'Weergavenaam (optioneel)',
    networkFolderSave: 'Opslaan',
    networkFolderSaved: 'Netwerkmap opgeslagen',
    networkFolderSavedUnreachable: 'Opgeslagen, maar de map is niet bereikbaar vanaf de server',
    networkFolderSaveFailed: 'Netwerkmap opslaan mislukt',
    networkFolderStatusOk: 'Verbonden',
    networkFolderStatusUnreachable: 'Niet bereikbaar',
    networkFolderStatusUnconfigured: 'Niet geconfigureerd',
    networkFolderSyncTitle: 'Kantoor-sync (online server)',
    networkFolderSyncDescription:
      'Op Hostinger kan de API Z:\\ niet direct lezen. In het tabblad Bestanden klikken medewerkers op Map kiezen (Chrome/Edge), selecteren de share één keer, daarna Nu synchroniseren. De map wordt op die PC onthouden.',
    statusLabel: 'Status',
    plannerNotesLabel: 'Planner notities',
    noPlannerNote: 'Geen planner notitie.',
    workerNotesLabel: 'Monteur notities',
    noWorkerNotesYet: 'Nog geen monteur notities.',
    noPhotosUploaded: 'Geen foto\'s geupload.',
    workCarriedOutLabel: 'Uitgevoerde werkzaamheden:',
    remainingWorkLabel: 'Nog uit te voeren werkzaamheden / restpunten:',
    requiredMaterialsInfo:
      'Gelieve hieronder aan te geven welke materialen reeds aanwezig zijn, welke nog besteld dienen te worden, welke materialen kunnen worden hergebruikt en of aanvullende hulpmaterialen benodigd zijn.',
    financialSettlementLabel: 'Financiele afwikkeling:',
    coverSheetTitle: 'Voorblad Werkvoorbereiding',
    clientLabel: 'Klant:',
    addressLabel: 'Adres:',
    postcodeCityLabel: 'Postcode/woonplaats',
    phoneLabel: 'Telefoonnummer',
    emailLabel: 'E-mail',
    colorSectionLabel: 'Kleur',
    exteriorFrameLabel: 'Kozijn buitenzijde',
    exteriorSashLabel: 'Draaideel buitenzijde',
    interiorFrameLabel: 'Kozijn binnenzijde',
    foilLabel: 'Folie:',
    yesLabel: 'ja',
    noLabel: 'nee',
    salesDateLabel: 'Verkoopdatum',
    installDateLabel: 'Montagedatum',
    measureDateLabel: 'Inmeetdatum',
    installDaysLabel: 'Montagedagen',
    ordersHeaderLabel: 'Bestellingen',
    orderedDateHeader: 'Besteldatum',
    deliveryDateHeader: 'Leverdatum',
    installersLabel: 'Monteurs:',

    // Teams page
    teamsAndWorkers: 'Teams & Medewerkers',
    manageTeams: 'Beheer uw leveringsteams en medewerkers',
    addWorker: 'Medewerker toevoegen',
    addTeam: 'Team toevoegen',
    noTeamsYet: 'Nog geen teams',
    noWorkersYet: 'Nog geen medewerkers',
    addNewTeam: 'Nieuw team toevoegen',
    teamName: 'Teamnaam',
    type: 'Type',
    individualWorker: 'Individuele medewerker',
    teamGroup: 'Teamgroep',
    teamColor: 'Teamkleur',
    members: 'Leden',
    createTeam: 'Team aanmaken',
    addNewWorker: 'Nieuwe medewerker toevoegen',
    name: 'Naam',
    phone: 'Telefoon',
    noContactInfo: 'Geen contactgegevens',
    workers: 'Medewerkers',
    prev: 'Vorige',
    next: 'Volgende',
    rowsPerPage: 'Rijen per pagina',
    page: 'Pagina',
    page: 'Pagina',
    teamNameRequired: 'Teamnaam is verplicht',
    teamCreatedSuccess: 'Team succesvol aangemaakt',
    teamCreateFailed: 'Team aanmaken mislukt',
    teamUpdatedSuccess: 'Team succesvol bijgewerkt',
    teamUpdateFailed: 'Team bijwerken mislukt',
    teamDeletedSuccess: 'Team verwijderd',
    teamDeleteFailed: 'Team verwijderen mislukt',
    workerNameRequired: 'Medewerkernaam is verplicht',
    workerAddedSuccess: 'Medewerker succesvol toegevoegd',
    workerAddFailed: 'Medewerker toevoegen mislukt',
    workerUpdatedSuccess: 'Medewerker succesvol bijgewerkt',
    workerUpdateFailed: 'Medewerker bijwerken mislukt',
    workerDeletedSuccess: 'Medewerker verwijderd',
    workerDeleteFailed: 'Medewerker verwijderen mislukt',
    confirmDeleteTeam: 'Weet je zeker dat je dit team wilt verwijderen?',
    confirmDeleteWorker: 'Weet je zeker dat je deze medewerker wilt verwijderen?',
    teamNamePlaceholder: 'bijv. Team Alpha',
    workerNamePlaceholder: 'Naam medewerker',
    workerPhonePlaceholder: '06-12345678',
    workerEmailPlaceholder: 'medewerker@email.com',
    editTeam: 'Team bewerken',
    editWorker: 'Medewerker bewerken',
    
    // Planning page
    week: 'Week',
    today: 'Vandaag',
    unassigned: 'Niet toegewezen',
    allOrdersAssigned: 'Alle bestellingen toegewezen!',
    addTeamsFirst: 'Voeg eerst teams toe',
    noTeamsConfigured: 'Nog geen teams geconfigureerd. Voeg teams toe op de Teams pagina om te beginnen met plannen.',
    planningWeekTotalLabel: 'Weektotaal (geplande orders)',
    planningMonthTotalLabel: 'Maandtotaal (geplande orders)',
    planningTotalExcludesMissing: 'excl. orders zonder opgeslagen bedrag',
    planningSearchPlaceholder: 'Zoek op klant, e-mail, postcode, tel., adres…',
    planningSearchNoResults: 'Geen geplande order gevonden in deze periode.',
    planningWorkday: 'werkdag',
    planningWorkdays: 'werkdagen',
    planningDragTeamRequired: 'Sleep naar een specifieke teamkolom',
    planningServiceQueue: 'Service',
    planningServiceAwaiting: 'Wacht op planning',
    planningServiceClearedForReschedule: 'Serviceplanning gewist — sleep naar een nieuwe dag om opnieuw te plannen.',
    planningWorkDaysUpdated: 'Werkdagen bijgewerkt',
    planningWorkDaysUpdateFailed: 'Werkdagen konden niet worden bijgewerkt',
    planningNoSidebarOrders: 'Geen orders die wachten op planning.',
    planningSidebarDays: 'Werkdagen',
    month: 'Maand',
    more: 'meer',

    // Days
    mon: 'Ma',
    tue: 'Di',
    wed: 'Wo',
    thu: 'Do',
    fri: 'Vr',
    sat: 'Za',
    sun: 'Zo',
    
    // Stats
    deliveriesMonth: 'Leveringen/Maand',
    activeTeams: 'Actieve Teams',
    onTimeRate: 'Op-tijd Score',

    // Offer detail (full page)
    backToOffers: 'Terug naar offertes',
    offerIdLabel: 'Offerte-ID',
    offerCreated: 'Aangemaakt',
    offerAccepted: 'Geaccepteerd',
    offerClientDetails: 'Klantgegevens',
    offerContactDetails: 'Contactgegevens',
    offerCopied: 'Offerte-ID gekopieerd',
    offerCopyFailed: 'Offerte-ID kopiëren mislukt',
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('vmk-language');
    return saved || 'nl';
  });

  useEffect(() => {
    localStorage.setItem('vmk-language', language);
  }, [language]);

  const t = (key, ...args) => {
    const val = translations[language][key];
    if (typeof val === 'function') return val(...args);
    return val ?? key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'nl' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
